import React from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import type { WeightSelection } from "../hooks/useBarChartData";

interface FormattingSectionProps {
  percentageFormatting: number;
  valuePlacement: "end" | "inside" | "none";
  weight: WeightSelection;
  weightsApplied: boolean;
  isRefetchingData: boolean;
  onPercentageFormattingChange: (value: number) => void;
  onValuePlacementChange: (value: "end" | "inside" | "none") => void;
  onWeightChange: (value: WeightSelection) => void;
}

const FormattingSection: React.FC<FormattingSectionProps> = ({
  percentageFormatting,
  valuePlacement,
  weight,
  weightsApplied,
  isRefetchingData,
  onPercentageFormattingChange,
  onValuePlacementChange,
  onWeightChange,
}) => {
  return (
    <Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <FormControl fullWidth size="small">
          <FormLabel className="text-[12px] text-[#262626]">Rounding</FormLabel>
          <Select
            value={percentageFormatting}
            onChange={(e) => onPercentageFormattingChange(e.target.value as number)}
          >
            <MenuItem value={0}>0 decimals</MenuItem>
            <MenuItem value={1}>1 decimal</MenuItem>
            <MenuItem value={2}>2 decimals</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <FormLabel className="text-[12px] text-[#262626]">
            Value placement
          </FormLabel>
          <Select
            value={valuePlacement}
            onChange={(e) =>
              onValuePlacementChange(e.target.value as "end" | "inside" | "none")
            }
          >
            <MenuItem value="end">End of bar</MenuItem>
            <MenuItem value="inside">Inside of bar</MenuItem>
            <MenuItem value="none">Don't display</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <FormLabel className="text-[12px] text-[#262626]">Weight</FormLabel>
          <Select
            value={weight}
            onChange={(e) => onWeightChange(e.target.value as WeightSelection)}
            disabled={isRefetchingData}
          >
            <MenuItem value="DISABLED">Disabled</MenuItem>
            <MenuItem value="DATASET_DEFAULT">Use Dataset Default</MenuItem>
          </Select>
        </FormControl>

        {weight === "DISABLED" && (
          <Alert
            severity="warning"
            icon={<WarningIcon fontSize="small" />}
            sx={{
              fontSize: "0.75rem",
              "& .MuiAlert-message": { fontSize: "0.75rem" },
            }}
          >
            Weights disabled: unweighted counts shown.
          </Alert>
        )}

        {weight === "DATASET_DEFAULT" && !weightsApplied && (
          <Alert
            severity="warning"
            icon={<WarningIcon fontSize="small" />}
            sx={{
              fontSize: "0.75rem",
              "& .MuiAlert-message": { fontSize: "0.75rem" },
            }}
          >
            No default weight configured for this dataset. Showing unweighted
            counts.
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default FormattingSection;
